/**
 * @fileoverview Input/output schemas for key-events-expander-v2.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterKeyEvents.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterProjectedWordCount.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterTitle.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterNumber.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterGoals.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPacing.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.previousChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  // Calculated fields passed from hook
  targetParagraphs: z.number().describe('The target number of paragraphs to generate.'),
  expansionMode: z.enum(['bring-to-standard', 'go-beyond-standard', 'generate-from-seed']).describe(
    'Which expansion mode to use. "bring-to-standard" fills sparse key events to baseline. ' +
    '"go-beyond-standard" extends adequate key events by ~50%. ' +
    '"generate-from-seed" develops a brief 1-2 sentence seed into a full beat sheet.'
  ),
  isBringToStandard: z.boolean().describe('Convenience flag: true when mode is bring-to-standard.'),
  isGenerateFromSeed: z.boolean().describe('Convenience flag: true when mode is generate-from-seed.'),
  // Optional context provided only when this expansion is part of a chapter
  // restructure. Gives the expander access to the planner's rationale and the
  // pre-restructure key events as source material to draw from.
  restructureContext: z.object({
    explanation: z.string().describe('The planner\'s explanation of what was split, merged, or reorganised, and why.'),
    originalChapters: z.array(z.object({
      number: z.number(),
      title: z.string(),
      keyEvents: z.string(),
    })).describe('The pre-restructure chapters that were replaced, in their original order.'),
  }).optional().describe('Optional source context from a chapter restructure. Only present when this expansion runs as part of the restructure flow.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  expandedKeyEvents: z.string().describe('The expanded key events as detailed prose paragraphs.'),
  commentary: z.string().optional().describe('Brief explanation of the expansion approach taken.'),
});

export type Output = z.infer<typeof OutputSchema>;
